import { Stack } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { LegalEntityPicker } from 'entities/legal-entities/legal-entity-picker';
import { FormData, formTools } from './supplier-invoice.form-tools';
import { TextField } from 'formik-mui';
import { UserPickerField } from 'features/user/user-picker/ui';
import { FormDialog } from 'shared/ui/form-dialog';
import { EntityRoles } from 'entities/invoice/types';
import { CompanyPickerField } from 'shared/ui/formik-fields/company-picker';
import { CONTRACT_TYPE_SUPPLIER, ContractInfoDto, INVOICE_PRINT_NOT_SHOW_DATE } from '@sr/dto';
import { ContractPicker } from 'entities/contract/contract-picker';
import { useMemo, useState } from 'react';
import { useBankDetailsList } from 'hooks/bank-details.hook';
import { BankDetailsPicker } from 'shared/ui/formik-fields/bank-details-picker';
import { ExternalPickerContainer } from 'components/Mui/external-picker-container';
import { BankDetailsDialog } from 'components/Companies/BankDetails/bank-details-dialog';
import { FormData as BankDetailsFormData } from 'components/Companies/BankDetails/bank-details.form';

const START = new Date();

const initialValues: FormData = {
  invoiceNumber: '',
  startDate: START,
  endDate: '',
  contractId: NaN,
  invoiceSupplier: true,
  buyerLegalEntityId: NaN,
  buyerBankDetailsId: null,
  sellerLegalEntityId: NaN,
  sellerBankDetailsId: null,
  sellerCompanyId: NaN,
  managerId: NaN,
  printFormShowMediaLink: false,
  printFormShowDateViewState: INVOICE_PRINT_NOT_SHOW_DATE
};
type Props = {
  open: boolean;
  supplierCompanyId?: number;
  ourCompanyId: number;
  onSubmit: (data: FormData) => Promise<void>;
  onClose: () => void;
  isCompanyPageMode: boolean;
};

export function AddSupplierInvoiceDialog(props: Props) {
  const { open, onSubmit, onClose, supplierCompanyId, ourCompanyId, isCompanyPageMode = true } = props;

  return (
    <FormDialog
      renderForm={() => <FormFields supplierCompanyId={isCompanyPageMode ? supplierCompanyId : undefined} ourCompanyId={ourCompanyId} />}
      renderTitle="Внести счет поставщика"
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      validate={formTools.validate}
      validateOnChange={true}
      initialValues={initialValues}
      dialogProps={{ maxWidth: 'sm', fullWidth: true }}
    />
  );
}

const FormFields = ({ supplierCompanyId, ourCompanyId }: { supplierCompanyId?: number; ourCompanyId: number;}) => {
  const { values, setFieldValue } = useFormikContext<FormData>();
  const buyerLegalEntityId = values.buyerLegalEntityId ?? undefined;
  const sellerLegalEntityId = (values as any)['sellerLegalEntityId'] ?? undefined;
  const buyerBankDetails = useBankDetailsList(!buyerLegalEntityId || isNaN(Number(buyerLegalEntityId)) ? undefined : Number(buyerLegalEntityId));
  const sellerBankDetails = useBankDetailsList(!sellerLegalEntityId || isNaN(Number(sellerLegalEntityId)) ? undefined : Number(sellerLegalEntityId));
  const [openBuyerBankDetailsDialog, setOpenBuyerBankDetailsDialog] = useState(false);
  const [openSellerBankDetailsDialog, setOpenSellerBankDetailsDialog] = useState(false);
  
  const companyId = useMemo(() => {
    return supplierCompanyId ?? values.sellerCompanyId ?? values.sellerLegalEntityId;
  }, [supplierCompanyId, values.sellerCompanyId, values.sellerLegalEntityId]);

  const handleContractChange = async (newContract: ContractInfoDto) => {
    if (!newContract?.buyer || !newContract?.seller) return;
    await setFieldValue('buyerLegalEntityId', newContract.buyer.id);
    await setFieldValue('buyerBankDetailsId', null);
    await setFieldValue('sellerLegalEntityId', newContract.seller.id);
    await setFieldValue('sellerBankDetailsId', null);
  };

  const handleAddBuyerBankDetails = async (formData: BankDetailsFormData) => {
    const created = await buyerBankDetails.addBankDetails(formData);
    await setFieldValue('buyerBankDetailsId', created.id);
    setOpenBuyerBankDetailsDialog(false);
  };

  const handleAddSellerBankDetails = async (formData: BankDetailsFormData) => {
    const created = await sellerBankDetails.addBankDetails(formData);
    await setFieldValue('sellerBankDetailsId', created.id);
    setOpenSellerBankDetailsDialog(false);
  };

  return (
    <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
      <Field name="invoiceNumber" component={TextField} label="Номер" required />
      <Stack direction="row" spacing={2}>
        <Field name="startDate" label="Дата выставления" component={DatePickerField} required />
      </Stack>
      {
        // если счет вносится вне компании поставщика, то выбрать компанию селектом
        !supplierCompanyId && <Field label="Фирма поставщика" component={CompanyPickerField} name="sellerCompanyId" legalEntityIdFilter={sellerLegalEntityId} required />
      }
      <Field name="contractId" label="Договор" component={ContractPicker} contractType={CONTRACT_TYPE_SUPPLIER} clientCompanyId={supplierCompanyId ?? companyId } onChange={handleContractChange} required />
      
      <Field
        disabled
        label="Наше юрлицо"
        disableInactive={true}
        component={LegalEntityPicker}
        name="buyerLegalEntityId"
        companyIdFilter={ourCompanyId}
        entityRole={EntityRoles.INVOICE_ACCEPTOR}
        required
      />
      <ExternalPickerContainer disabled={!buyerLegalEntityId || isNaN(Number(buyerLegalEntityId))} onClick={() => setOpenBuyerBankDetailsDialog(true)}>
        <Field
          label="Наши банковские реквизиты"
          component={BankDetailsPicker}
          name="buyerBankDetailsId"
          legalEntityId={Number(buyerLegalEntityId)}
          collection={buyerBankDetails.collection}
        />
      </ExternalPickerContainer>
      <Field disabled label="Юрлицо поставщика" disableInactive={true} component={LegalEntityPicker} name="sellerLegalEntityId" companyIdFilter={supplierCompanyId ?? companyId} required />
      <ExternalPickerContainer disabled={!sellerLegalEntityId || isNaN(Number(sellerLegalEntityId))} onClick={() => setOpenSellerBankDetailsDialog(true)}>
        <Field
          label="Реквизиты продавца"
          component={BankDetailsPicker}
          name="sellerBankDetailsId"
          legalEntityId={Number(sellerLegalEntityId)}
          collection={sellerBankDetails.collection}
        />
      </ExternalPickerContainer>

      <Field name="managerId" label="Менеджер" component={UserPickerField} includeUser required />
      <BankDetailsDialog
        open={openBuyerBankDetailsDialog}
        title="Добавить банковские реквизиты"
        onClose={() => setOpenBuyerBankDetailsDialog(false)}
        submit={handleAddBuyerBankDetails}
      />
      <BankDetailsDialog
        open={openSellerBankDetailsDialog}
        title="Добавить банковские реквизиты"
        onClose={() => setOpenSellerBankDetailsDialog(false)}
        submit={handleAddSellerBankDetails}
      />
    </Stack>
  );
};
